import { EditOutlined, FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Input, InputNumber, message, Modal, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useQuery } from 'react-query';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CreateSegmentModal from './CreateSegmentModal';
import { BaggageRouteTariffModel, BaggageTypeModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageBaggageTariff } from '@/hooks/useHasPermissions';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { formatCurrency } from '@/helpers/format';

const PAGE_TITLE = 'baggageTariffsPage_Title';

/** Franchise et maximum de la compagnie. */
const SettingsTab = ({ canUpdate }: { canUpdate: boolean }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { isLoading } = useQuery(['company_baggage_settings'], () => apiSdk.BaggageRulesService.settings(), {
    onSuccess: data => form.setFieldsValue(data),
  });

  const save = (values: any) => {
    setSaving(true);
    apiSdk.BaggageRulesService.settings1({ body: { freeBaggageCount: values.freeBaggageCount, maxBaggagePerTicket: values.maxBaggagePerTicket ?? undefined } })
      .then(response => {
        if (response.success) {
          message.success(t('baggageRules_Saved'));
        } else {
          message.error(t(response.message || ''));
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setSaving(false));
  };

  return (
    <Spin spinning={isLoading}>
      <Typography.Paragraph type="secondary">{t('baggageRules_SettingsIntro')}</Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={save} style={{ maxWidth: 360 }}>
        <Form.Item label={t('baggageRules_FreeCount')} name="freeBaggageCount" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
          <InputNumber min={0} max={99} precision={0} disabled={!canUpdate} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label={t('baggageRules_MaxPerTicket')} name="maxBaggagePerTicket">
          <InputNumber min={1} max={999} precision={0} disabled={!canUpdate} style={{ width: '100%' }} />
        </Form.Item>
        {canUpdate ? (
          <Button type="primary" htmlType="submit" className="save-btn" icon={<SaveOutlined />} loading={saving}>
            {t('save')}
          </Button>
        ) : null}
      </Form>
    </Spin>
  );
};

/** Types de bagages de la compagnie. */
const TypesTab = ({ canUpdate, onChanged }: { canUpdate: boolean; onChanged: () => void }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<BaggageTypeModel | 'new'>();
  const [saving, setSaving] = useState(false);
  const { data, isLoading, refetch } = useQuery(['company_baggage_types'], () => apiSdk.BaggageRulesService.types());

  const open = (type: BaggageTypeModel | 'new') => {
    setEditing(type);
    form.setFieldsValue(type === 'new' ? { name: undefined, countsAgainstAllowance: true, isActive: true, displayOrder: (data?.length || 0) + 1 } : type);
  };

  const save = (values: any) => {
    setSaving(true);
    apiSdk.BaggageRulesService.types1({ body: { ...values, id: editing === 'new' ? undefined : editing?.id } })
      .then(response => {
        if (response.success) {
          setEditing(undefined);
          refetch();
          onChanged();
        } else {
          message.error(t(response.message || ''));
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setSaving(false));
  };

  const columns = [
    { title: t('baggageRules_TypeName'), dataIndex: 'name' },
    {
      title: t('baggageRules_InAllowance'),
      dataIndex: 'countsAgainstAllowance',
      render: (v: boolean) => (v ? <Tag color="green">{t('baggageRules_InAllowanceYes')}</Tag> : <Tag>{t('baggageRules_AlwaysPaid')}</Tag>),
    },
    {
      title: t('baggageRules_Active'),
      dataIndex: 'isActive',
      render: (v: boolean) => (v ? <Tag color="green">{t('baggageRules_ActiveYes')}</Tag> : <Tag color="red">{t('baggageRules_ActiveNo')}</Tag>),
    },
    { title: t('baggageRules_Order'), dataIndex: 'displayOrder', width: 90 },
    ...(canUpdate
      ? [
          {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: any, r: BaggageTypeModel) => <Button size="small" icon={<EditOutlined />} onClick={() => open(r)} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">{t('baggageRules_TypesIntro')}</Typography.Paragraph>
      {canUpdate ? (
        <Button type="primary" className="nta-create-btn" icon={<PlusCircleOutlined />} onClick={() => open('new')} style={{ marginBottom: 16 }}>
          {t('baggageRules_AddType')}
        </Button>
      ) : null}
      <Table rowKey="id" loading={isLoading} dataSource={data || []} columns={columns as any} pagination={false} bordered size="small" />
      <Modal
        visible={!!editing}
        title={t(editing === 'new' ? 'baggageRules_AddType' : 'baggageRules_EditType')}
        width={ModalWidth.small}
        okText={t('save')}
        cancelText={t('cancel')}
        confirmLoading={saving}
        onCancel={() => setEditing(undefined)}
        onOk={() => form.validateFields().then(save).catch(() => {})}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('baggageRules_TypeName')} name="name" rules={[{ required: true, whitespace: true, message: VALIDATE_REQUIRED }]}>
            <Input maxLength={60} />
          </Form.Item>
          <Form.Item name="countsAgainstAllowance" valuePropName="checked">
            <Checkbox>{t('baggageRules_InAllowanceHelp')}</Checkbox>
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked">
            <Checkbox>{t('baggageRules_ActiveHelp')}</Checkbox>
          </Form.Item>
          <Form.Item label={t('baggageRules_Order')} name="displayOrder">
            <InputNumber min={0} max={999} precision={0} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

/** Tarifs par type et par parcours ; « sans tarif » : la liste des parcours à compléter. */
const TariffsTab = ({ canUpdate, types }: { canUpdate: boolean; types: BaggageTypeModel[] }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [missingOnly, setMissingOnly] = useState(false);
  const [searchText, setSearchText] = useState<string>();
  const [editing, setEditing] = useState<BaggageRouteTariffModel>();
  const [saving, setSaving] = useState(false);
  const [displaySegmentModal, setDisplaySegmentModal] = useState(false);
  const { data, isLoading, refetch } = useQuery(['company_baggage_tariffs', missingOnly, searchText], () =>
    apiSdk.BaggageRulesService.tariffs({ missingOnly, searchText }),
  );
  const activeTypes = types.filter(x => x.isActive);

  const open = (route: BaggageRouteTariffModel) => {
    setEditing(route);
    const values: Record<string, number | undefined> = {};
    route.amounts?.forEach(x => {
      values[x.baggageTypeId || ''] = x.amount ?? undefined;
    });
    form.resetFields();
    form.setFieldsValue(values);
  };

  const save = (values: Record<string, number | undefined | null>) => {
    if (!editing) return;
    setSaving(true);
    apiSdk.BaggageRulesService.tariffs1({
      body: {
        sourceId: editing.sourceId,
        destinationId: editing.destinationId,
        amounts: activeTypes.map(x => ({ baggageTypeId: x.id, amount: values[x.id || ''] ?? undefined })),
      },
    })
      .then(response => {
        if (response.success) {
          setEditing(undefined);
          refetch();
        } else {
          message.error(t(response.message || ''));
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setSaving(false));
  };

  const columns = [
    { title: t('baggageTariffsPage_From'), dataIndex: 'sourceName' },
    { title: t('baggageTariffsPage_To'), dataIndex: 'destinationName' },
    ...activeTypes.map(type => ({
      title: type.name,
      key: type.id,
      render: (_: any, r: BaggageRouteTariffModel) => {
        const amount = r.amounts?.find(x => x.baggageTypeId === type.id)?.amount;
        return amount === undefined || amount === null ? <Tag color="red">{t('baggageRules_NoTariff')}</Tag> : formatCurrency(amount);
      },
    })),
    ...(canUpdate
      ? [
          {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: any, r: BaggageRouteTariffModel) => <Button size="small" icon={<EditOutlined />} onClick={() => open(r)} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search allowClear placeholder={t('baggageTariffsPage_SearchText')} onSearch={v => setSearchText(v || undefined)} style={{ width: 260 }} />
        <Checkbox checked={missingOnly} onChange={e => setMissingOnly(e.target.checked)} data-testid="missing-only">
          {t('baggageRules_MissingOnly')}
        </Checkbox>
        {canUpdate ? (
          <Button icon={<FlagOutlined />} onClick={() => setDisplaySegmentModal(true)}>
            {t('baggageTariffsPage_CreateNewSegment')}
          </Button>
        ) : null}
      </Space>
      <Table rowKey={(r: BaggageRouteTariffModel) => `${r.sourceId}-${r.destinationId}`} loading={isLoading} dataSource={data || []} columns={columns as any} pagination={{ pageSize: 25 }} bordered size="small" />
      <Modal
        visible={!!editing}
        title={t('baggageRules_EditTariffs', { from: editing?.sourceName, to: editing?.destinationName })}
        width={ModalWidth.small}
        okText={t('save')}
        cancelText={t('cancel')}
        confirmLoading={saving}
        onCancel={() => setEditing(undefined)}
        onOk={() => form.validateFields().then(save).catch(() => {})}
      >
        <Typography.Paragraph type="secondary">{t('baggageRules_EmptyMeansNoTariff')}</Typography.Paragraph>
        <Form form={form} layout="vertical">
          {activeTypes.map(type => (
            <Form.Item key={type.id} label={type.name} name={type.id || ''}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          ))}
        </Form>
      </Modal>
      {displaySegmentModal ? (
        <CreateSegmentModal
          onClose={() => {
            setDisplaySegmentModal(false);
            refetch();
          }}
        />
      ) : null}
    </>
  );
};

const BaggageTariffsPage = () => {
  const { t } = useTranslation();
  const canUpdate = !!useCanManageBaggageTariff();
  const { data: types, refetch } = useQuery(['company_baggage_types_for_tariffs'], () => apiSdk.BaggageRulesService.types());

  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Tabs
          defaultActiveKey="tariffs"
          items={[
            { key: 'tariffs', label: t('baggageRules_TabTariffs'), children: <TariffsTab canUpdate={canUpdate} types={types || []} /> },
            { key: 'types', label: t('baggageRules_TabTypes'), children: <TypesTab canUpdate={canUpdate} onChanged={() => refetch()} /> },
            { key: 'settings', label: t('baggageRules_TabSettings'), children: <SettingsTab canUpdate={canUpdate} /> },
          ]}
        />
      </div>
    </MainContent>
  );
};

export default BaggageTariffsPage;
